import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EmptyState, InfiniteScroll } from 'common/components';
import { LIST_LIMIT_0 } from 'common/config';
import { AuthHooks } from 'common/hooks';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  CatalogCategoryList,
  CatalogGoodsListFilters,
  CatalogHeader,
  CatalogNavigation,
  CatalogTitle,
} from 'entities/Catalog/components';
import { getCatalogGoodsAvailabilityFilters } from 'entities/Catalog/Catalog.helper';
import { ICategoryListParams } from 'entities/Category/Category.models';
import { getCategoriesGoodsCountItems, getCategoryList, selectCategoryList } from 'entities/Category/Category.slice';
import { selectCurrentUser } from 'entities/User/User.slice';
import { ICatalog } from 'entities/Catalog//Catalog.models';
import { setPropertyList } from 'entities/Property/Property.slice';

interface IComponentProps {
  catalog: ICatalog;
}

export const CatalogRootPage: FC<IComponentProps> = ({ catalog }) => {
  const dispatch = useAppDispatch();
  // category
  const categoryList = useAppSelector(selectCategoryList);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [categoryListParams, setCategoryListParams] = useState<ICategoryListParams | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isSeller } = AuthHooks.useIsSeller();

  useEffect(() => {
    const fetch = async () => {
      dispatch(setPropertyList([]));
      const { isAvailableForCustomer } = getCatalogGoodsAvailabilityFilters(location, isSeller, currentUser?.account);
      const categoryListParams: ICategoryListParams = {
        catalogId: catalog.id,
        withoutParents: true,
        isAvailableForCustomer,
        limit: LIST_LIMIT_0,
      };

      setCategoryListParams(categoryListParams);
      const categories = await dispatch(getCategoryList(categoryListParams));

      if (categories) {
        const categoryIds = categories.map((item) => item.id);

        await dispatch(
          getCategoriesGoodsCountItems({
            catalogId: catalog.id,
            categoryIds,
            isAvailableForCustomer,
          }),
        );
      }

      setFetchLoading(false);
    };

    fetch();
  }, [catalog, currentUser?.city?.id]);

  const onAvailableForCustomerFilterChange = async (value: boolean) => {
    if (!categoryListParams) {
      return;
    }

    const newCategoryListParams = {
      ...categoryListParams,
      isAvailableForCustomer: value,
    };

    navigate(location, {
      replace: true,
      state: {
        isAvailableForCustomer: newCategoryListParams.isAvailableForCustomer,
        isAvailableForPurchase: value ? true : undefined,
      },
    });
    setCategoryListParams(newCategoryListParams);
    const categories = await dispatch(getCategoryList(newCategoryListParams));

    if (categories) {
      const categoryIds = categories.map((item) => item.id);

      await dispatch(
        getCategoriesGoodsCountItems({
          catalogId: catalog.id,
          categoryIds,
          isAvailableForCustomer: newCategoryListParams.isAvailableForCustomer,
        }),
      );
    }
  };

  return (
    <div className="redesign relative pt-11 px-11 h-full flex direction-column">
      <CatalogHeader catalog={catalog} />

      <InfiniteScroll>
        <div className="mx-11 flex direction-column flex-1">
          <CatalogNavigation catalog={catalog} />

          <CatalogTitle catalogName={catalog.name} />

          <div className="mb-14">
            <CatalogGoodsListFilters
              isAvailableForCustomer={categoryListParams?.isAvailableForCustomer}
              onAvailableForCustomerChange={onAvailableForCustomerFilterChange}
            />
          </div>

          <CatalogCategoryList catalogId={catalog.id} />

          {!fetchLoading && !categoryList.length && (
            <EmptyState
              title="В этом каталоге товары отсутствуют."
              description="Они появятся здесь после добавления поставщиком."
            />
          )}
        </div>
      </InfiniteScroll>
    </div>
  );
};
